/**
 * Icon Positioning Utils
 * Détection automatique et repositionnement conditionnel des icônes et labels
 * masqués par des popins ou autres contraintes de layout
 */

interface ViewportBounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

interface ElementRect {
  left: number;
  right: number;
  top: number;
  bottom: number;
  width: number;
  height: number;
}

/**
 * Gets the border radius offset for positioning buttons outside rounded corners
 * Returns the extra offset needed based on the parent's border-radius
 */
function getBorderRadiusOffset(element: HTMLElement): { top: number; right: number; bottom: number; left: number } {
  const ownerDoc = element.ownerDocument;
  const defaultOffset = { top: 0, right: 0, bottom: 0, left: 0 };
  
  // Check #spm_content specifically for teaser circle mode
  const spmContent = ownerDoc.querySelector('#spm_content') as HTMLElement;
  if (spmContent) {
    const computedStyle = ownerDoc.defaultView?.getComputedStyle(spmContent);
    if (computedStyle) {
      const borderRadius = computedStyle.borderRadius;
      if (borderRadius && borderRadius !== '0px') {
        const radiusValue = parseFloat(borderRadius);
        const contentRect = spmContent.getBoundingClientRect();
        const minDimension = Math.min(contentRect.width, contentRect.height);
        const isCircular = radiusValue >= minDimension / 2 || borderRadius.includes('50%');
        
        if (isCircular) {
          // For circular elements, use larger offset to clear the curve completely
          const cornerOffset = Math.ceil(minDimension * 0.15);
          return { top: cornerOffset, right: cornerOffset, bottom: cornerOffset, left: cornerOffset };
        } else if (radiusValue > 10) {
          const cornerOffset = Math.ceil(radiusValue * 0.15);
          return { top: cornerOffset, right: cornerOffset, bottom: cornerOffset, left: cornerOffset };
        }
      }
    }
  }
  
  return defaultOffset;
}

/**
 * Gets the viewport bounds, using iframe dimensions if element is inside an iframe
 */
function getViewportBounds(element: HTMLElement): ViewportBounds {
  // Check if element is inside an iframe
  const ownerDoc = element.ownerDocument;
  const ownerWindow = ownerDoc.defaultView;
  
  if (ownerWindow && ownerWindow !== window) {
    // Element is inside an iframe, use iframe dimensions
    return {
      left: 0,
      right: ownerWindow.innerWidth,
      top: 0,
      bottom: ownerWindow.innerHeight
    };
  }
  
  // Element is in main document
  return {
    left: 0,
    right: window.innerWidth,
    top: 0,
    bottom: window.innerHeight
  };
}

/**
 * Detects which edges of the element overflow the viewport
 */
function getOverflowEdges(element: HTMLElement): { left: boolean; right: boolean; top: boolean; bottom: boolean } {
  const rect = element.getBoundingClientRect();
  const viewport = getViewportBounds(element);

  return {
    left: rect.left < viewport.left,
    right: rect.right > viewport.right,
    top: rect.top < viewport.top,
    bottom: rect.bottom > viewport.bottom
  };
}

/**
 * Checks if an element is hidden or outside the viewport (even partially)
 * @param skipVisibilityCheck - Skip visibility check during repositioning
 */
function isElementHidden(element: HTMLElement, skipVisibilityCheck = false): boolean {
  const rect = element.getBoundingClientRect();
  const viewport = getViewportBounds(element);

  // Check if element overflows viewport (even partially)
  const isOutOfViewport = (
    rect.left < viewport.left ||
    rect.right > viewport.right ||
    rect.top < viewport.top ||
    rect.bottom > viewport.bottom
  );

  const hasZeroSize = rect.width === 0 || rect.height === 0;
  const isInvisible = !skipVisibilityCheck && element.style.display === 'none';

  return isOutOfViewport || hasZeroSize || isInvisible;
}

/**
 * Repositions an element based on which edges overflow the viewport
 */
function repositionElement(element: HTMLElement): boolean {
  const originalStyles = {
    left: element.style.left,
    right: element.style.right,
    top: element.style.top,
    bottom: element.style.bottom,
    flexDirection: element.style.flexDirection,
    marginTop: element.style.marginTop,
    transform: element.style.transform,
    opacity: element.style.opacity
  };

  // Hide element during repositioning to avoid flicker (use opacity to keep dimensions)
  element.style.opacity = '0';

  const isLabel = element.classList.contains('spm-label-wrapper');
  const isIcon = element.classList.contains('actions-buttons-group') || 
                 element.className.endsWith('-actions-buttons-group') ||
                 element.id === 'widget-actions-buttons-group';

  // Detect which edges overflow
  const overflow = getOverflowEdges(element);

  if (isLabel) {
    // Labels: reposition based on overflow
    if (overflow.left) {
      element.style.left = 'auto';
      element.style.right = '0';
    }
    if (overflow.right) {
      element.style.left = '0';
      element.style.right = 'auto';
    }
    if (overflow.top) {
      element.style.top = 'calc(100% + 4px)';
      element.style.bottom = 'auto';
    }
    if (overflow.bottom) {
      element.style.top = 'auto';
      element.style.bottom = 'calc(100% + 4px)';
    }
    
    if (!isElementHidden(element, true)) {
      element.style.opacity = '';
      return true;
    }

  } else if (isIcon) {
    // Get dynamic offset based on parent's border-radius
    const radiusOffset = getBorderRadiusOffset(element);
    const hasSignificantRadius = radiusOffset.top > 0 || radiusOffset.right > 0;
    
    // For circular/rounded containers, choose best position based on available space
    // Use a small gap (4px) to keep toolbar reachable on hover — at the vertical/horizontal
    // center of a circle, the edge aligns with the bounding box so no large radius offset needed
    if (hasSignificantRadius) {
      const elementRect = element.parentElement?.getBoundingClientRect();
      const viewport = getViewportBounds(element);

      if (elementRect) {
        const spaceRight = viewport.right - elementRect.right;
        const spaceLeft = elementRect.left - viewport.left;
        const spaceTop = elementRect.top - viewport.top;
        const spaceBottom = viewport.bottom - elementRect.bottom;

        const sideGap = 2;
        // Measure toolbar in row orientation for accurate vertical offset
        const origFlex = element.style.flexDirection;
        element.style.flexDirection = 'row';
        const rowHeight = element.getBoundingClientRect().height;
        element.style.flexDirection = origFlex;
        const verticalGap = Math.ceil(rowHeight);

        // Candidates ordered by available space (most space first)
        const candidates = [
          { space: spaceRight, minSpace: 60, left: `calc(100% + ${sideGap}px)`, right: 'auto', top: '50%', bottom: 'auto', flexDirection: 'column', transform: 'translateY(-50%)' },
          { space: spaceLeft, minSpace: 60, left: 'auto', right: `calc(100% + ${sideGap}px)`, top: '50%', bottom: 'auto', flexDirection: 'column', transform: 'translateY(-50%)' },
          { space: spaceTop, minSpace: 50, left: '50%', right: 'auto', top: `-${verticalGap}px`, bottom: 'auto', flexDirection: 'row', transform: 'translateX(-50%)' },
          { space: spaceBottom, minSpace: 50, left: '50%', right: 'auto', top: 'auto', bottom: `-${verticalGap}px`, flexDirection: 'row', transform: 'translateX(-50%)' },
        ].sort((a, b) => b.space - a.space);

        for (const candidate of candidates) {
          if (candidate.space < candidate.minSpace) continue;

          element.style.left = candidate.left;
          element.style.right = candidate.right;
          element.style.top = candidate.top;
          element.style.bottom = candidate.bottom;
          element.style.flexDirection = candidate.flexDirection;
          element.style.transform = candidate.transform;

          if (!isElementHidden(element, true)) {
            element.style.opacity = '';
            return true;
          }
        }
      }
      // If no circular position worked, fall through to standard positions
    }
    
    // Measure toolbar in both orientations to get accurate offsets
    const origFlexDir = element.style.flexDirection;
    element.style.flexDirection = 'row';
    const rowH = element.getBoundingClientRect().height;
    element.style.flexDirection = 'column';
    const colW = element.getBoundingClientRect().width;
    element.style.flexDirection = origFlexDir;

    const baseTopOffset = Math.ceil(rowH);
    const baseRightOffset = Math.ceil(colW) + 2;
    const baseBottomOffset = Math.ceil(rowH);
    const baseLeftOffset = Math.ceil(colW) + 2;
    
    // Standard positions for non-rounded elements
    const positions = [
      // Top, horizontal, centered
      { left: '50%', right: 'auto', top: `-${baseTopOffset}px`, bottom: 'auto', flexDirection: 'row', transform: 'translateX(-50%)' },
      // Top, horizontal, left aligned
      { left: '0', right: 'auto', top: `-${baseTopOffset}px`, bottom: 'auto', flexDirection: 'row', transform: '' },
      // Top, horizontal, right aligned
      { left: 'auto', right: '0', top: `-${baseTopOffset}px`, bottom: 'auto', flexDirection: 'row', transform: '' },
      // Right side, vertical
      { left: 'auto', right: `-${baseRightOffset}px`, top: '4px', bottom: 'auto', flexDirection: 'column', transform: '' },
      // Bottom, horizontal, centered
      { left: '50%', right: 'auto', top: 'auto', bottom: `-${baseBottomOffset}px`, flexDirection: 'row', transform: 'translateX(-50%)' },
      // Left side, vertical
      { left: `-${baseLeftOffset}px`, right: 'auto', top: '4px', bottom: 'auto', flexDirection: 'column', transform: '' },
    ];

    for (const pos of positions) {
      element.style.left = pos.left;
      element.style.right = pos.right;
      element.style.top = pos.top;
      element.style.bottom = pos.bottom;
      element.style.flexDirection = pos.flexDirection;
      element.style.transform = pos.transform;
      
      if (!isElementHidden(element, true)) {
        element.style.opacity = '';
        return true;
      }
    }
  }

  // No strategy worked, restore original styles
  Object.assign(element.style, originalStyles);
  element.style.opacity = '';
  return false;
}

/**
 * Resets an element to its default position
 */
function resetElementPosition(element: HTMLElement): void {
  if (element.classList.contains('actions-buttons-group') || 
      element.className.endsWith('-actions-buttons-group')) {
    
    // Default position for icons: left side, vertical
    element.style.left = '-50px';
    element.style.right = 'auto';
    element.style.top = '4px';
    element.style.bottom = 'auto';
    element.style.flexDirection = 'column';
    element.style.marginTop = '';
    element.style.transform = '';
    
  } else if (element.classList.contains('spm-label-wrapper')) {
    
    // Default position for labels: top left
    element.style.left = '0';
    element.style.right = 'auto';
    element.style.top = 'auto';
    element.style.bottom = 'calc(100% + 4px)';
    element.style.flexDirection = '';
    element.style.marginTop = '';
    element.style.transform = '';
  }
}

/**
 * Processes all relevant elements (icons and labels)
 */
export function repositionIfHidden(): void {
  const selectors = [
    '.actions-buttons-group',
    '#widget-actions-buttons-group', 
    '[class$="-actions-buttons-group"]',
    '.spm-label-wrapper'
  ];

  selectors.forEach(selector => {
    const elements = document.querySelectorAll<HTMLElement>(selector);
    
    elements.forEach(element => {
      if (isElementHidden(element)) {
        repositionElement(element);
      } else {
        // If element is visible, ensure it's at default position
        resetElementPosition(element);
      }
    });
  });
}

/**
 * Version debounced pour éviter trop d'appels lors du resize
 */
let repositionTimeout: number | null = null;
let iconPositioningObserver: MutationObserver | null = null;
function debouncedRepositionIfHidden(): void {
  if (repositionTimeout) {
    clearTimeout(repositionTimeout);
  }
  
  repositionTimeout = window.setTimeout(() => {
    repositionIfHidden();
    repositionTimeout = null;
  }, 100);
}

/**
 * Initialise les événements de repositionnement automatique
 */
export function initIconPositioning(): void {
  // Cleanup previous instance (re-entrance safety)
  cleanupIconPositioning();

  // Repositionner au chargement
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', repositionIfHidden);
  } else {
    repositionIfHidden();
  }

  // Repositionner sur redimensionnement de fenêtre
  window.addEventListener('resize', debouncedRepositionIfHidden);

  // Observer les mutations DOM pour les éléments ajoutés dynamiquement
  iconPositioningObserver = new MutationObserver((mutations) => {
    let shouldReposition = false;
    
    mutations.forEach(mutation => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            
            // Vérifier si l'élément ajouté ou ses enfants correspondent aux sélecteurs
            const hasTargetElements = (
              element.matches('.actions-buttons-group, #widget-actions-buttons-group, [class$="-actions-buttons-group"], .spm-label-wrapper') ||
              element.querySelector('.actions-buttons-group, #widget-actions-buttons-group, [class$="-actions-buttons-group"], .spm-label-wrapper')
            );
            
            if (hasTargetElements) {
              shouldReposition = true;
            }
          }
        });
      }
    });
    
    if (shouldReposition) {
      debouncedRepositionIfHidden();
    }
  });

  iconPositioningObserver.observe(document.body, {
    childList: true,
    subtree: true
  });

}

/**
 * Manually repositions a specific element
 */
export function repositionSpecificElement(element: HTMLElement): boolean {
  // Hide immediately to avoid flicker during position testing
  element.style.opacity = '0';
  
  if (isElementHidden(element, true)) {
    return repositionElement(element);
  }
  
  // Element already visible, restore opacity
  element.style.opacity = '';
  return true;
}

/**
 * Nettoyage des événements (si nécessaire)
 */
export function cleanupIconPositioning(): void {
  window.removeEventListener('resize', debouncedRepositionIfHidden);
  if (iconPositioningObserver) {
    iconPositioningObserver.disconnect();
    iconPositioningObserver = null;
  }
  if (repositionTimeout) {
    clearTimeout(repositionTimeout);
    repositionTimeout = null;
  }
}
